import { Button } from "@/components/design-system/button";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import { cn } from "@/utils/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm, type FieldErrors } from "react-hook-form";
import { useLocation } from "react-router";
import { ADMIN_CLASS_USE_MOCK } from "../config/admin.config";
import { adminClassToFormValues } from "../api/adminClass.mappers";
import { useClassCatalogOptions } from "../hooks/useClassCatalogOptions";
import { useSchoolCourseOptions } from "../hooks/useSchoolCourseOptions";
import { useSchoolDropdownOptions } from "../hooks/useSchoolDropdownOptions";
import type { LinkableGrade } from "../types/class.types";
import type { AdminClass } from "../types/class.types";
import { classFormSchema, type ClassFormSchemaInput, type ClassFormSchemaValues } from "../schemas/classForm.schema";
import { computeTotalPrice } from "../utils/class.utils";
import { CLASS_LIMITS } from "../constants/class.constants";
import { bindDescriptionTextField, bindTitleTextField, bindTrimmedTextField } from "@/utils/formTextField";
import { showApiErrorToast } from "../utils/apiToastMessage";

import { getSchoolService } from "@/service/school.service";
import ClassThumbnailUploadSection from "./ClassThumbnailUploadSection";

const uid = () => Math.random().toString(36).slice(2, 10);

function toNumberInputValue(value: unknown): string | number {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "number" || typeof value === "string") return value;
  return "";
}

const emptyDefaults: ClassFormSchemaInput = {
  name: "",
  description: "",
  validityDays: "",
  price: "",
  taxPercent: "",
  level1Id: "",
  level2Id: "",
  level3Id: "",
  benefits: [{ id: `b-${uid()}`, title: "", description: "" }],
  schoolId: "",
  schoolName: "",
  adminName: "",
  schoolTitle: "Admin",
  email: "",
  contactNumber: "",
  licenseCountTeacher: 0,
  licenseCountStudent: 0,
  courseId: "",
  location: {
    address: "",
    district: "",
    city: "",
    state: "",
    pincode: "",
  },
  thumbnailUrl: "",
  uploadId: "",
};

function CharCounter({
  current,
  max,
}: {
  current: number;
  max: number;
}) {
  return (
    <span
      className={cn(
        "text-[12px] font-medium leading-normal",
        current > max ? "text-[#ef4444]" : "text-[#A0AEC0]"
      )}
    >
      {current} / {max}
    </span>
  );
}

const FORM_LABEL_CLASS =
  "mb-[6px] text-[16px] font-medium leading-normal text-[#2D3748]";
const PAGE_TITLE_CLASS =
  "mb-8 text-[20px] font-bold leading-[1.5] text-[#1F1F1F]";
const SECTION_TITLE_CLASS =
  "mb-4 text-[20px] font-bold leading-[1.5] text-[#1F1F1F]";
const BENEFIT_POINT_TITLE_CLASS =
  "mb-3 text-[18px] font-bold leading-[1.5] text-[#1F1F1F]";
const INPUT_BASE_CLASS =
  "w-full h-[50px] rounded-[15px] border border-[#E2E8F0] px-[20px] text-[16px] font-medium text-[#2E2E32] placeholder:text-[14px] placeholder:font-medium placeholder:text-[#A0AEC0] focus:outline-none";
const TEXTAREA_BASE_CLASS =
  "w-full min-h-[120px] rounded-[15px] border border-[#E2E8F0] px-[20px] pt-[13px] pb-8 text-[16px] font-medium text-[#2E2E32] placeholder:text-[14px] placeholder:font-medium placeholder:text-[#A0AEC0] resize-y focus:outline-none";

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className={FORM_LABEL_CLASS}>
      {children}
      {required ? <span className="text-[#ef4444]">*</span> : null}
    </div>
  );
}

function SectionDivider() {
  return <div className="my-8 h-px w-full bg-[#EDEDED]" aria-hidden="true" />;
}

const CLASS_FORM_FIELD_ORDER = [
  "name",
  "description",
  "validityDays",
  "price",
  "taxPercent",
  "level1Id",
  "level2Id",
  "level3Id",
] as const;

function hasFieldError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  return Boolean(
    "message" in error &&
      typeof (error as { message?: unknown }).message === "string" &&
      (error as { message: string }).message
  );
}

function getBenefitsErrorPath(
  benefitsErrors: FieldErrors<ClassFormSchemaInput>["benefits"]
): string | null {
  if (!benefitsErrors) return null;
  if (hasFieldError(benefitsErrors)) return "benefits";
  if ("root" in benefitsErrors && hasFieldError(benefitsErrors.root)) {
    return "benefits";
  }
  if (Array.isArray(benefitsErrors)) {
    for (let i = 0; i < benefitsErrors.length; i++) {
      const item = benefitsErrors[i];
      if (hasFieldError(item?.title)) return `benefits.${i}.title`;
      if (hasFieldError(item?.description)) return `benefits.${i}.description`;
    }
  }
  return null;
}

function getFirstClassFormErrorPath(
  formErrors: FieldErrors<ClassFormSchemaInput>
): string | null {
  for (const field of CLASS_FORM_FIELD_ORDER) {
    if (hasFieldError(formErrors[field])) return field;
  }

  const benefitsPath = getBenefitsErrorPath(formErrors.benefits);
  if (benefitsPath) return benefitsPath;

  for (const field of [
    "schoolId",
    "schoolName",
    "adminName",
    "email",
    "contactNumber",
    "courseId",
    "thumbnailUrl",
    "uploadId",
  ] as const) {
    if (hasFieldError(formErrors[field])) return field;
  }

  return null;
}

function scrollToClassFormField(fieldName: string) {
  requestAnimationFrame(() => {
    document
      .querySelector(`[data-form-field="${fieldName}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

interface ClassFormProps {
  mode: "create" | "edit" | "view";
  initial?: AdminClass | null;
  grades: LinkableGrade[];
  gradesLoading?: boolean;
  onSubmit?: (values: ClassFormSchemaValues) => Promise<void>;
  onClose: () => void;
  submitLabel?: string;
}

export default function ClassForm({
  mode,
  initial,
  grades,
  gradesLoading = false,
  onSubmit,
  onClose,
  submitLabel,
}: ClassFormProps) {
  const readOnly = mode === "view";
  const levelsReadOnly = readOnly;
  const schoolReadOnly = readOnly;
  const loadSchoolOptions = !ADMIN_CLASS_USE_MOCK && !readOnly;
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const location = useLocation();
  const {
    catalogLevels,
    level2Options: flatLevel2Options,
    level3Options,
    levelCascade,
    loading: catalogLoading,
    refreshCatalog,
  } = useClassCatalogOptions();
  const { schools: schoolOptions, loading: schoolsLoading } =
    useSchoolDropdownOptions(loadSchoolOptions);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm<ClassFormSchemaInput, unknown, ClassFormSchemaValues>({
    resolver: zodResolver(classFormSchema),
    defaultValues: initial ? adminClassToFormValues(initial) : emptyDefaults,
    mode: "onChange",
  });

  const schoolId = watch("schoolId");
  const { grades: schoolGrades, loading: schoolGradesLoading } =
    useSchoolCourseOptions(schoolId, loadSchoolOptions);
  const activeGrades = loadSchoolOptions ? schoolGrades : grades;
  const activeGradesLoading = loadSchoolOptions ? schoolGradesLoading : gradesLoading;

  useEffect(() => {
    if (initial) {
      reset(adminClassToFormValues(initial));
    }
  }, [initial, reset]);

  const benefitsArray = useFieldArray({ control, name: "benefits" });
  const benefitsAtMax = benefitsArray.fields.length >= CLASS_LIMITS.benefitsMax;

  useEffect(() => {
    refreshCatalog();
  }, [location.pathname, mode, refreshCatalog]);

  const level1Id = watch("level1Id");
  const level2Id = watch("level2Id");
  const level3Id = watch("level3Id");
  const uploadId = watch("uploadId");
  const schoolDetailsReadOnly = schoolReadOnly || loadSchoolOptions;

  useEffect(() => {
    if (readOnly || catalogLoading) return;
    const l1 = catalogLevels.find((l) => l.id === level1Id);
    if (level1Id && !l1) {
      setValue("level1Id", "");
      setValue("level2Id", "");
    } else if (
      levelCascade &&
      level2Id &&
      l1 &&
      !l1.levels.some((l2) => l2.id === level2Id)
    ) {
      setValue("level2Id", "");
    }
    if (level3Id && !level3Options.some((o) => o.id === level3Id)) {
      setValue("level3Id", "");
    }
  }, [
    catalogLevels,
    level3Options,
    level1Id,
    level2Id,
    level3Id,
    levelCascade,
    readOnly,
    catalogLoading,
    setValue,
  ]);

  const price = watch("price");
  const taxPercent = watch("taxPercent");
  const nameVal = watch("name");
  const descVal = watch("description");

  const level2Options = useMemo(() => {
    if (!levelCascade) {
      return flatLevel2Options.map((l) => ({ id: l.id, label: l.label }));
    }
    const parent = catalogLevels.find((l) => l.id === level1Id);
    return parent?.levels ?? [];
  }, [level1Id, catalogLevels, flatLevel2Options, levelCascade]);

  const level1SelectOptions = useMemo(() => {
    const options = catalogLevels.map((l) => ({
      value: l.id,
      label: l.label,
    }));
    const savedId = initial?.levels.level1Id;
    const savedLabel = initial?.levels.level1Label;
    if (savedId && !options.some((o) => o.value === savedId)) {
      return [{ value: savedId, label: savedLabel || savedId }, ...options];
    }
    return options;
  }, [catalogLevels, initial]);

  const level2SelectOptions = useMemo(() => {
    const options = level2Options.map((l) => ({
      value: l.id,
      label: l.label,
    }));
    const savedId = initial?.levels.level2Id;
    const savedLabel = initial?.levels.level2Label;
    if (savedId && !options.some((o) => o.value === savedId)) {
      return [{ value: savedId, label: savedLabel || savedId }, ...options];
    }
    return options;
  }, [level2Options, initial]);

  const level3SelectOptions = useMemo(() => {
    const options = level3Options.map((o) => ({
      value: o.id,
      label: o.label,
    }));
    const savedId = initial?.levels.level3Id;
    const savedLabel = initial?.levels.level3Label;
    if (savedId && !options.some((o) => o.value === savedId)) {
      return [{ value: savedId, label: savedLabel || savedId }, ...options];
    }
    return options;
  }, [level3Options, initial]);

  const schoolSelectOptions = useMemo(() => {
    const options = schoolOptions.map((s) => ({
      value: s.id,
      label: s.name,
    }));
    const savedId = initial?.schoolId;
    const savedLabel = initial?.school.schoolName;
    if (savedId && !options.some((o) => o.value === savedId)) {
      return [{ value: savedId, label: savedLabel || savedId }, ...options];
    }
    return options;
  }, [schoolOptions, initial]);

  const courseSelectOptions = useMemo(() => {
    const options = activeGrades.map((g) => ({
      value: g.id,
      label: g.name,
    }));
    const savedId = initial?.courseId ?? initial?.school.linkedGradeIds[0];
    if (savedId && !options.some((o) => o.value === savedId)) {
      const savedLabel =
        initial?.courseName ??
        activeGrades.find((g) => g.id === savedId)?.name ??
        savedId;
      return [{ value: savedId, label: savedLabel }, ...options];
    }
    return options;
  }, [activeGrades, initial]);

  const totalPrice = useMemo(() => {
    if (price === "" || price === null || price === undefined) return null;
    if (taxPercent === "" || taxPercent === null || taxPercent === undefined) {
      return null;
    }
    const p = Number(price);
    const t = Number(taxPercent);
    if (Number.isNaN(p) || Number.isNaN(t)) return null;
    return computeTotalPrice(p, t);
  }, [price, taxPercent]);

  useEffect(() => {
    if (readOnly || catalogLoading) return;
    if (!levelCascade || !level1Id) return;
    const valid = level2Options.some((l) => l.id === watch("level2Id"));
    if (!valid) {
      setValue("level2Id", "");
    }
  }, [
    level1Id,
    level2Options,
    levelCascade,
    readOnly,
    catalogLoading,
    setValue,
    watch,
  ]);

  const handleSchoolSelectById = async (id: string) => {
    const school = schoolSelectOptions.find((option) => option.value === id);
    setValue("schoolName", school?.label ?? "", { shouldValidate: true });
    setValue("courseId", "");

    if (ADMIN_CLASS_USE_MOCK || !id) return;

    try {
      const { data } = await getSchoolService(id);
      const details = data.data;
      if (!details) {
        return;
      }

      setValue("adminName", details.user?.name ?? "");
      setValue("email", details.user?.email ?? "");
      setValue("contactNumber", details.user?.contact_number ?? "");
      setValue("schoolTitle", details.user?.title ?? "Admin");
      setValue("licenseCountTeacher", details.no_of_teacher_licence ?? 0);
      setValue("licenseCountStudent", details.no_of_student_licence ?? 0);
      setValue(
        "location",
        details.location ?? {
          address: "",
          district: "",
          city: "",
          state: "",
          pincode: "",
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  const submitHandler = handleSubmit(
    async (values) => {
      if (readOnly || !onSubmit) return;
      await onSubmit(values);
    },
    (formErrors) => {
      const firstField = getFirstClassFormErrorPath(formErrors);
      if (firstField) {
        scrollToClassFormField(firstField);

        const firstFieldError = firstField
          .split(".")
          .reduce<unknown>((acc, key) => {
            if (!acc || typeof acc !== "object") return undefined;
            return (acc as Record<string, unknown>)[key];
          }, formErrors);

        const message =
          firstFieldError &&
          typeof firstFieldError === "object" &&
          "message" in firstFieldError &&
          typeof (firstFieldError as { message?: unknown }).message === "string"
            ? (firstFieldError as { message: string }).message
            : "Please fix the highlighted field and try again.";

        showApiErrorToast({ message });
      }
    }
  );

  return (
    <div className="bg-white rounded-[12px] w-full overflow-hidden">
      <div className="px-6 md:px-[32px] py-6">
        <h2 className={PAGE_TITLE_CLASS}>
          {mode === "create" ? "Add Class" : mode === "edit" ? "Edit Class" : "View Class"}
        </h2>

        <section className="space-y-4">
          <div data-form-field="name">
            <FieldLabel required>Class Name</FieldLabel>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <div>
                  <div className="relative">
                    <input
                      {...bindTitleTextField(field)}
                      disabled={readOnly}
                      placeholder="Enter Class Name"
                      className={cn(
                        INPUT_BASE_CLASS,
                        "pl-[20px] pr-16",
                        errors.name ? "border-[#ef4444]" : "border-[#E2E8F0]"
                      )}
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      <CharCounter
                        current={(nameVal ?? "").length}
                        max={CLASS_LIMITS.nameMax}
                      />
                    </div>
                  </div>
                  {errors.name?.message ? (
                    <p className="text-[12px] text-[#ef4444] mt-1">{errors.name.message}</p>
                  ) : null}
                </div>
              )}
            />
          </div>

          <div data-form-field="description">
            <FieldLabel required>Class Description</FieldLabel>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <div>
                  <div className="relative">
                    <textarea
                      {...bindDescriptionTextField(field)}
                      disabled={readOnly}
                      rows={4}
                      placeholder="Enter Class Description"
                      className={cn(
                        TEXTAREA_BASE_CLASS,
                        "pl-[20px] pr-16",
                        errors.description ? "border-[#ef4444]" : "border-[#E2E8F0]"
                      )}
                    />
                    <div className="pointer-events-none absolute bottom-3 right-3">
                      <CharCounter
                        current={(descVal ?? "").length}
                        max={CLASS_LIMITS.descriptionMax}
                      />
                    </div>
                  </div>
                  {errors.description?.message ? (
                    <p className="text-[12px] text-[#ef4444] mt-1">
                      {errors.description.message}
                    </p>
                  ) : null}
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div data-form-field="validityDays">
              <FieldLabel required>Validity (in days)</FieldLabel>
              <Controller
                control={control}
                name="validityDays"
                render={({ field }) => (
                  <input
                    {...field}
                    value={toNumberInputValue(field.value)}
                    disabled={readOnly}
                    type="number"
                      min={1}
                      step={1}
                    placeholder="Enter Validity in days"
                    className={cn(
                      INPUT_BASE_CLASS,
                      errors.validityDays ? "border-[#ef4444]" : "border-[#E2E8F0]"
                    )}
                  />
                )}
              />
              {errors.validityDays?.message ? (
                <p className="text-[12px] text-[#ef4444] mt-1">
                  {errors.validityDays.message}
                </p>
              ) : null}
            </div>
            <div data-form-field="price">
              <FieldLabel required>Class Price</FieldLabel>
              <Controller
                control={control}
                name="price"
                render={({ field }) => (
                  <div className="relative">
                    <span className="absolute left-[20px] top-1/2 -translate-y-1/2 text-[16px] font-medium text-[#2E2E32]">
                      ₹
                    </span>
                    <input
                      {...field}
                      value={toNumberInputValue(field.value)}
                      disabled={readOnly}
                      type="number"
                      min={0}
                      step={1}
                      placeholder="Enter Class Price"
                      className={cn(
                        INPUT_BASE_CLASS,
                        "pl-8 pr-[20px]",
                        errors.price ? "border-[#ef4444]" : "border-[#E2E8F0]"
                      )}
                    />
                  </div>
                )}
              />
              {errors.price?.message ? (
                <p className="text-[12px] text-[#ef4444] mt-1">{errors.price.message}</p>
              ) : null}
            </div>
            <div data-form-field="taxPercent">
              <FieldLabel required>Tax Percentage</FieldLabel>
              <Controller
                control={control}
                name="taxPercent"
                render={({ field }) => (
                  <input
                    {...field}
                    value={toNumberInputValue(field.value)}
                    disabled={readOnly}
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    placeholder="Enter Tax Percentage"
                    className={cn(
                      INPUT_BASE_CLASS,
                      errors.taxPercent ? "border-[#ef4444]" : "border-[#E2E8F0]"
                    )}
                  />
                )}
              />
              {errors.taxPercent?.message ? (
                <p className="text-[12px] text-[#ef4444] mt-1">
                  {errors.taxPercent.message}
                </p>
              ) : null}
            </div>
          </div>

          <div className="max-w-md">
            <FieldLabel>Total Price</FieldLabel>
            <div className="relative">
              <span className="pointer-events-none absolute left-[20px] top-1/2 z-[1] -translate-y-1/2 select-none text-[16px] font-medium text-[#2E2E32]">
                ₹
              </span>
              <div
                className="flex h-[50px] w-full items-center rounded-[15px] border border-[#E2E8F0] bg-[#F9FAFC] pl-8 pr-[20px] text-[16px] font-medium pointer-events-none select-none"
                aria-readonly="true"
              >
                <span
                  className={cn(
                    totalPrice != null ? "text-[#2E2E32]" : "text-[#A0AEC0]"
                  )}
                >
                  {totalPrice != null ? totalPrice : "-"}
                </span>
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        <section>
          <h3 className={SECTION_TITLE_CLASS}>Set Levels</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div data-form-field="level1Id">
              <FieldLabel required>Level 1</FieldLabel>
              <Controller
                control={control}
                name="level1Id"
                render={({ field }) => (
                    <FilterWithRadio
                      options={level1SelectOptions}
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                        setValue("level2Id", "");
                      }}
                      placeholder="Select Level 1"
                      disabled={levelsReadOnly}
                      showAbove={false}
                      fieldStyle
                      error={errors.level1Id?.message}
                    />
                )}
              />
            </div>
            <div data-form-field="level2Id">
              <FieldLabel required>Level 2</FieldLabel>
              <Controller
                control={control}
                name="level2Id"
                render={({ field }) => (
                    <FilterWithRadio
                      options={level2SelectOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select Level 2"
                      disabled={levelsReadOnly || (levelCascade && !level1Id)}
                      showAbove={false}
                      fieldStyle
                      error={errors.level2Id?.message}
                    />
                )}
              />
            </div>
            <div data-form-field="level3Id">
              <FieldLabel required>Level 3</FieldLabel>
              <Controller
                control={control}
                name="level3Id"
                render={({ field }) => (
                    <FilterWithRadio
                      options={level3SelectOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select Level 3"
                      disabled={levelsReadOnly}
                      showAbove={false}
                      fieldStyle
                      error={errors.level3Id?.message}
                    />
                )}
              />
            </div>
          </div>
        </section>

        <SectionDivider />

        <section data-form-field="benefits">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[20px] font-bold leading-[1.5] text-[#1F1F1F] shrink-0">
              Add Benefits
            </h3>
            {!readOnly ? (
              <button
                type="button"
                className="inline-flex h-[32px] w-[98px] shrink-0 items-center justify-center rounded-[70px] border border-[#5C88C5] bg-white px-[10px] text-[16px] font-medium leading-none text-[#5C88C5] transition-colors hover:bg-[#F5FBFF] disabled:pointer-events-none disabled:opacity-50"
                disabled={benefitsAtMax}
                onClick={() => {
                  if (benefitsAtMax) return;
                  benefitsArray.append({
                    id: `b-${uid()}`,
                    title: "",
                    description: "",
                  });
                }}
              >
                Add
              </button>
            ) : null}
          </div>
          <div className="space-y-6">
            {benefitsArray.fields.map((item, index) => {
              const titleError = errors.benefits?.[index]?.title?.message;
              const descriptionError = errors.benefits?.[index]?.description?.message;

              return (
              <div key={item.id} className="relative">
                <div className={BENEFIT_POINT_TITLE_CLASS}>
                  Point {index + 1}
                </div>
                <div className="grid grid-cols-1 gap-4 pr-10">
                  <div data-form-field={`benefits.${index}.title`}>
                    <FieldLabel required={!readOnly}>Title</FieldLabel>
                    <Controller
                      control={control}
                      name={`benefits.${index}.title`}
                      render={({ field }) => (
                        <div>
                          <div className="relative">
                            <input
                              {...bindTitleTextField(field)}
                              disabled={readOnly}
                              placeholder="Enter Name"
                              className={cn(
                                INPUT_BASE_CLASS,
                                "pl-[20px] pr-16",
                                titleError ? "border-[#ef4444]" : "border-[#E2E8F0]"
                              )}
                            />
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <CharCounter
                                current={(field.value ?? "").length}
                                max={CLASS_LIMITS.benefitTitleMax}
                              />
                            </div>
                          </div>
                          {titleError ? (
                            <p className="text-[12px] text-[#ef4444] mt-1">{titleError}</p>
                          ) : null}
                        </div>
                      )}
                    />
                  </div>
                  <div data-form-field={`benefits.${index}.description`}>
                    <FieldLabel required={!readOnly}>Description</FieldLabel>
                    <Controller
                      control={control}
                      name={`benefits.${index}.description`}
                      render={({ field }) => (
                        <div>
                          <div className="relative">
                            <textarea
                              {...bindDescriptionTextField(field)}
                              disabled={readOnly}
                              rows={3}
                              placeholder="Enter Description"
                              className={cn(
                                TEXTAREA_BASE_CLASS,
                                "pl-[20px] pr-16",
                                descriptionError ? "border-[#ef4444]" : "border-[#E2E8F0]"
                              )}
                            />
                            <div className="pointer-events-none absolute bottom-3 right-3">
                              <CharCounter
                                current={(field.value ?? "").length}
                                max={CLASS_LIMITS.benefitDescriptionMax}
                              />
                            </div>
                          </div>
                          {descriptionError ? (
                            <p className="text-[12px] text-[#ef4444] mt-1">{descriptionError}</p>
                          ) : null}
                        </div>
                      )}
                    />
                  </div>
                </div>
                {!readOnly && benefitsArray.fields.length > 1 ? (
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#FF152C] p-2"
                    onClick={() => benefitsArray.remove(index)}
                    aria-label="Remove benefit"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                ) : null}
              </div>
            );
            })}
          </div>
          {errors.benefits?.message ? (
            <p className="text-[12px] text-[#ef4444] mt-2">{errors.benefits.message}</p>
          ) : null}
          {errors.benefits?.root?.message ? (
            <p className="text-[12px] text-[#ef4444] mt-2">{errors.benefits.root.message}</p>
          ) : null}
        </section>

        <SectionDivider />

        <section>
          <h3 className={SECTION_TITLE_CLASS}>School Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div data-form-field={loadSchoolOptions ? "schoolId" : "schoolName"}>
              <FieldLabel required={!schoolReadOnly}>
                {loadSchoolOptions ? "School" : "School Name"}
              </FieldLabel>
              {loadSchoolOptions ? (
                <Controller
                  control={control}
                  name="schoolId"
                  render={({ field }) => (
                    <FilterWithRadio
                      options={schoolSelectOptions}
                      value={field.value ?? ""}
                      onChange={(id) => {
                        field.onChange(id);
                        void handleSchoolSelectById(id);
                      }}
                      placeholder={
                        schoolsLoading ? "Loading schools…" : "Select school"
                      }
                      disabled={schoolReadOnly || schoolsLoading}
                      showAbove={false}
                      fieldStyle
                      error={errors.schoolId?.message}
                    />
                  )}
                />
              ) : (
                <Controller
                  control={control}
                  name="schoolName"
                  render={({ field }) => (
                    <input
                      {...bindTrimmedTextField(field)}
                      disabled={schoolReadOnly}
                      placeholder="Enter Name"
                      className={cn(
                        INPUT_BASE_CLASS,
                        errors.schoolName ? "border-[#ef4444]" : "border-[#E2E8F0]"
                      )}
                    />
                  )}
                />
              )}
            </div>
            <div data-form-field="adminName">
              <FieldLabel required={!schoolDetailsReadOnly}>
                School Admin Name
              </FieldLabel>
              <Controller
                control={control}
                name="adminName"
                render={({ field }) => (
                  <input
                    {...bindTrimmedTextField(field)}
                    disabled={schoolDetailsReadOnly}
                    placeholder="Enter Title"
                    className={cn(
                      INPUT_BASE_CLASS,
                      errors.adminName ? "border-[#ef4444]" : "border-[#E2E8F0]"
                    )}
                  />
                )}
              />
            </div>
            <div data-form-field="email">
              <FieldLabel required={!schoolDetailsReadOnly}>Email Address</FieldLabel>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <input
                    {...bindTrimmedTextField(field)}
                    disabled={schoolDetailsReadOnly}
                    placeholder="Enter Email Address"
                    className={cn(
                      INPUT_BASE_CLASS,
                      errors.email ? "border-[#ef4444]" : "border-[#E2E8F0]"
                    )}
                  />
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-form-field="contactNumber">
              <FieldLabel required={!schoolDetailsReadOnly}>Contact Number</FieldLabel>
              <Controller
                control={control}
                name="contactNumber"
                render={({ field }) => (
                  <input
                    {...bindTrimmedTextField(field)}
                    disabled={schoolDetailsReadOnly}
                    placeholder="Enter Contact Number"
                    className={cn(
                      INPUT_BASE_CLASS,
                      errors.contactNumber ? "border-[#ef4444]" : "border-[#E2E8F0]"
                    )}
                  />
                )}
              />
            </div>
            <div data-form-field="courseId">
              <FieldLabel required={!schoolReadOnly}>Course</FieldLabel>
              <Controller
                control={control}
                name="courseId"
                render={({ field }) => (
                  <FilterWithRadio
                    options={courseSelectOptions}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder={
                      activeGradesLoading
                        ? "Loading courses…"
                        : loadSchoolOptions && !schoolId?.trim()
                          ? "Select school first"
                          : "Choose Course"
                    }
                    disabled={
                      schoolReadOnly ||
                      activeGradesLoading ||
                      (loadSchoolOptions && !schoolId?.trim())
                    }
                    showAbove={false}
                    fieldStyle
                    error={errors.courseId?.message}
                  />
                )}
              />
            </div>
          </div>
        </section>

        <SectionDivider />

        <div data-form-field="thumbnailUrl">
          <Controller
            name="thumbnailUrl"
            control={control}
            render={({ field }) => (
              <ClassThumbnailUploadSection
                value={field.value}
                cropSourceUrl={initial?.thumbnailCropUrl}
                uploadId={uploadId}
                onChange={(url) => field.onChange(url)}
                onUploadIdChange={(id) => setValue("uploadId", id, { shouldValidate: true })}
                onUploadingChange={setThumbnailUploading}
                readOnly={readOnly}
                error={errors.thumbnailUrl?.message || errors.uploadId?.message}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end gap-4 border-t border-[#EDEDED] px-6 md:px-[32px] py-4">
        <Button
          type="button"
          variant="light"
          size="md"
          className="w-[120px] h-[48px] border border-[#5C88C5]"
          onClick={onClose}
        >
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly ? (
          <Button
            type="button"
            variant="primary"
            size="md"
            className="w-[140px] h-[48px] bg-[#5C88C5] text-white"
            disabled={isSubmitting || thumbnailUploading}
            onClick={submitHandler}
          >
            {isSubmitting
              ? "Saving…"
              : thumbnailUploading
                ? "Uploading…"
                : submitLabel ?? (mode === "create" ? "Add" : "Update")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
